import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { picksAPI } from '../services/api';
import PickBreakdown from '../components/PickBreakdown';

function MyPicks() {
  const navigate = useNavigate();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadPicks = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter === 'all' ? {} : { status: filter };
      const response = await picksAPI.getMyPicks(params);
      
      if (response.success) {
        setPicks(response.data.picks || []);
      }
    } catch (error) {
      console.error('Failed to load picks:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadPicks();
  }, [loadPicks]);

  const handleDeletePick = async (pickId, raceName) => {
    if (!window.confirm(`Delete pick for ${raceName}?`)) {
      return;
    }

    try {
      const response = await picksAPI.deletePick(pickId);
      if (response.success) {
        // Reload picks
        loadPicks();
      }
    } catch (error) {
      alert(error.response?.data?.error?.message || 'Failed to delete pick');
    }
  };

  const calculateStats = () => {
    const completed = picks.filter((p) => p.status === 'completed');
    const wins = completed.filter((p) => p.actualPayout > 0);
    const totalWinnings = completed.reduce((sum, p) => sum + (p.actualPayout || 0), 0);
    const totalBet = picks.reduce((sum, p) => sum + (p.betAmount || 10.0), 0);
    
    return {
      totalPicks: picks.length,
      completedPicks: completed.length,
      pendingPicks: picks.filter((p) => p.status === 'pending').length,
      wins: wins.length,
      winRate: completed.length > 0 ? ((wins.length / completed.length) * 100).toFixed(1) : 0,
      totalWinnings: totalWinnings.toFixed(2),
      totalBet: totalBet.toFixed(2),
      profit: (totalWinnings - totalBet).toFixed(2),
    };
  };

  if (loading) {
    return <div className="loading">Loading your picks...</div>;
  }

  const stats = calculateStats();

  return (
    <div className="my-picks-page">
      <h2>My Picks</h2>

      {/* Stats Card */}
      <div className="card">
        <h3>Your Pick Statistics</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
              {stats.totalPicks}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Total Picks</p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
              {stats.pendingPicks}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Pending</p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50', margin: 0 }}>
              {stats.wins}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Wins</p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
              {stats.winRate}%
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Win Rate</p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50', margin: 0 }}>
              ${stats.totalWinnings}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Total Winnings</p>
          </div>
          <div>
            <p style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: parseFloat(stats.profit) >= 0 ? '#4CAF50' : '#f44336',
              margin: 0
            }}>
              ${stats.profit}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Net Profit/Loss</p>
          </div>
        </div>
      </div>

      {/* Picks Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>All Picks ({picks.length})</h3>
          
          <div>
            <label htmlFor="filter" style={{ marginRight: '0.5rem' }}>Filter:</label>
            <select
              id="filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '1rem',
              }}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {picks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            <p>No picks found.</p>
            <button
              onClick={() => navigate('/races')}
              className="submit-button"
              style={{ maxWidth: '200px', margin: '1rem auto' }}
            >
              Browse Races
            </button>
          </div>
        ) : (
          <table className="horses-table">
            <thead>
              <tr>
                <th>Race</th>
                <th>Horses</th>
                <th>Picked At</th>
                <th>Bet</th>
                <th>Status</th>
                <th>Payout</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {picks.map((pick) => {
                const canDelete = pick.status === 'pending';
                const horsesList = pick.horses?.map(h => h.horseName).join(', ') || pick.horseName || 'N/A';
                const hasBreakdown = pick.actualPayout > 0 && pick.payoutBreakdown;
                
                return (
                  <React.Fragment key={pick.pickId}>
                    <tr>
                      <td>
                        <span
                          style={{ cursor: 'pointer', color: '#2196F3', textDecoration: 'underline' }}
                          onClick={() => navigate(`/races/${pick.raceId}`)}
                        >
                          {pick.raceName}
                        </span>
                      </td>
                      <td>
                        <strong>{horsesList}</strong>
                        {pick.actualPayout > 0 && <span style={{ marginLeft: '0.5rem', color: '#4CAF50' }}>🏆</span>}
                        {pick.comment && (
                          <div style={{ fontSize: '0.85rem', color: '#555', fontStyle: 'italic', marginTop: '0.25rem' }}>
                           &ldquo;{pick.comment}&rdquo;
                          </div>
                        )}
                      </td>
                      <td>{new Date(pick.submittedAt).toLocaleDateString()}</td>
                      <td>${(pick.betAmount || 10.0).toFixed(2)}</td>
                      <td>
                        <span className={`race-status ${pick.status}`}>
                          {pick.status}
                        </span>
                      </td>
                      <td>
                        {pick.status === 'completed' ? (
                          <strong style={{ color: pick.actualPayout > 0 ? '#4CAF50' : '#f44336' }}>
                            ${(pick.actualPayout || 0).toFixed(2)}
                          </strong>
                        ) : (
                          <span style={{ color: '#666' }}>
                            {pick.potentialPayout ? `$${pick.potentialPayout.toFixed(2)}` : 'TBD'}
                          </span>
                        )}
                      </td>
                      <td>
                        {canDelete ? (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => navigate(`/races/${pick.raceId}?edit=${pick.pickId}`)}
                              style={{
                                background: '#2196F3',
                                color: 'white',
                                border: 'none',
                                padding: '0.4rem 0.8rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeletePick(pick.pickId, pick.raceName)}
                              style={{
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                padding: '0.4rem 0.8rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: '#999', fontSize: '0.85rem' }}>-</span>
                        )}
                      </td>
                    </tr>
                    {hasBreakdown && (
                      <tr>
                        <td colSpan="7" style={{ padding: '0 1rem 1rem 1rem', backgroundColor: '#fafafa' }}>
                          <PickBreakdown pick={pick} expanded={false} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default MyPicks;
